/**
 * @file
 * Products and Services tabbed module with accordion sub-items.
 *
 * Tabs:
 *   - Pre-rendered HTML panels, JS toggles .is-active; CSS crossfades opacity (300ms).
 *   - Switching tabs always resets the new panel to its default state
 *     (description visible, all accordion items collapsed).
 *
 * Accordion (within each tab panel):
 *   - Only one accordion item open at a time.
 *   - Opening an item hides the description wrapper (.products-and-services__description).
 *   - Closing the open item (clicking it again) restores the description.
 *   - Open/close animates via CSS grid-template-rows transition (driven by aria-expanded).
 */

(function (Drupal) {

  'use strict';

  Drupal.behaviors.productsAndServices = {
    /**
     * Attaches the products-and-services tab and accordion behaviour.
     *
     * @param {HTMLElement} context - The DOM context provided by Drupal.attach().
     * @param {object} settings - Drupal settings object (unused).
     * @return {void}
     */
    attach: function (context, settings) {

      const wrappers = context.querySelectorAll('[data-products-tabs]');

      wrappers.forEach(function (wrapper) {

        // Guard against double-attachment on the same element.
        if (wrapper.dataset.psInitialized) return;
        wrapper.dataset.psInitialized = 'true';

        const tabs   = Array.from(wrapper.querySelectorAll('.products-and-services__tab'));
        const panels = Array.from(wrapper.querySelectorAll('.products-and-services__panel'));

        // ------------------------------------------------------------------
        // Helpers
        // ------------------------------------------------------------------

        /**
         * Reset a panel to its default state:
         * description wrapper visible, all accordion items collapsed.
         *
         * @param {HTMLElement} panel - The tab panel element to reset.
         * @return {void}
         */
        function resetPanel(panel) {
          const descWrapper = panel.querySelector('.products-and-services__description');
          if (descWrapper) {
            descWrapper.classList.remove('is-hidden');
          }

          panel.querySelectorAll('.products-and-services__accordion-header').forEach(function (header) {
            header.setAttribute('aria-expanded', 'false');
          });
        }

        /**
         * Activate a tab panel by zero-based index, deactivating all others.
         *
         * @param {number} index - Zero-based index of the tab to activate.
         * @return {void}
         */
        function activateTab(index) {
          tabs.forEach(function (tab, i) {
            const isActive = i === index;
            tab.classList.toggle('is-active', isActive);
            tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
          });

          panels.forEach(function (panel, i) {
            const isActive = i === index;

            if (isActive) {
              // Reset accordion state before revealing the panel.
              resetPanel(panel);
              panel.classList.add('is-active');
              panel.setAttribute('aria-hidden', 'false');
            } else {
              panel.classList.remove('is-active');
              panel.setAttribute('aria-hidden', 'true');
            }
          });
        }

        // ------------------------------------------------------------------
        // Tab click handlers
        // ------------------------------------------------------------------
        tabs.forEach(function (tab, index) {
          tab.addEventListener('click', function () {
            activateTab(index);
          });
        });

        // ------------------------------------------------------------------
        // Item title click – closes all accordions and restores description
        // ------------------------------------------------------------------
        panels.forEach(function (panel) {
          const itemTitle = panel.querySelector('.products-and-services__item-title');
          if (itemTitle) {
            itemTitle.addEventListener('click', function () {
              resetPanel(panel);
            });
          }
        });

        // ------------------------------------------------------------------
        // Accordion handlers – scoped to each panel
        // ------------------------------------------------------------------
        panels.forEach(function (panel) {

          const accordionItems = panel.querySelectorAll('[data-accordion-index]');
          const descWrapper    = panel.querySelector('.products-and-services__description');

          accordionItems.forEach(function (item) {
            const header = item.querySelector('.products-and-services__accordion-header');
            if (!header) return;

            header.addEventListener('click', function () {
              const isOpen = header.getAttribute('aria-expanded') === 'true';

              if (isOpen) {
                // Close this item → restore default state for the whole panel
                header.setAttribute('aria-expanded', 'false');

                if (descWrapper) {
                  descWrapper.classList.remove('is-hidden');
                }
              } else {
                // Close any currently-open accordion item in this panel first
                panel.querySelectorAll('.products-and-services__accordion-header[aria-expanded="true"]')
                  .forEach(function (openHeader) {
                    openHeader.setAttribute('aria-expanded', 'false');
                  });

                // Open this item and hide the description wrapper
                header.setAttribute('aria-expanded', 'true');

                if (descWrapper) {
                  descWrapper.classList.add('is-hidden');
                }
              }
            });
          });
        });

        // Ensure first tab is in the correct state on page load.
        resetPanel(panels[0]);
      });
    }
  };

})(Drupal);
